import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from '@amzn/awsui-components-core';
import {
  FeedbackItem,
  FeedbackItemStatistics,
  FeedbackMetadata,
  FeedbackResourceType,
} from '@amzn/awscat-pattern-library-service-model';

import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../api/FeedbackService';
import { FeedbackRatingSystem, FeedbackRating, FeedbackSource } from './FeedbackTypes';
import { AppContext } from '../../contexts/AppContext';
import { localLog } from '../../loggers/Logger';
import FeedbackModal from './FeedbackModal';

const votedSvgPathMap = new Map([
  [FeedbackRating.Like, '/images/thumb-up-voted.svg'],
  [FeedbackRating.Dislike, '/images/thumb-down-voted.svg'],
]);

const unvotedSvgPathMap = new Map([
  [FeedbackRating.Like, '/images/thumb-up.svg'],
  [FeedbackRating.Dislike, '/images/thumb-down.svg'],
]);

const ratingTypesMap = new Map([
  [FeedbackRatingSystem.Like, [FeedbackRating.Like]],
  [FeedbackRatingSystem.LikeDislike, [FeedbackRating.Like, FeedbackRating.Dislike]],
]);

interface ContentFeedbackProps {
  readonly contentId: string;
  /** The type of the rated resource; a collection's ID is passed as `contentId` too. */
  readonly resourceType: FeedbackResourceType;
  /** The content version being viewed, stored with a content rating. */
  readonly workflowId?: number;
  readonly ratingSystem: FeedbackRatingSystem;
  readonly onRating?: (contentId: string, feedbackRating: FeedbackRating) => void;
  readonly feedbackSource?: FeedbackSource;

  /**
   * After the user finishes casting likes/dislikes, whether to show a
   * "request feedback" modal.
   *
   * As we don't support providing written feedback for 'posts' contents (a.k.a.
   * contents from the "what's new") menu, set this to `false` for them,
   * and `true` for the rest.
   *
   * @default true
   */
  readonly requestWrittenFeedback?: boolean;
}

const ContentFeedback = ({
  contentId,
  resourceType,
  workflowId,
  ratingSystem,
  onRating,
  feedbackSource = FeedbackSource.Guidance,
  requestWrittenFeedback = true,
}: ContentFeedbackProps) => {
  const feedbackServiceClient: IFeedbackServiceClient = useMemo(
    () => FeedbackServiceFactory.create(),
    [],
  );
  const [feedbackStatistics, setFeedbackStatistics] = useState<FeedbackItemStatistics | null>(null);
  const [userFeedbackItem, setUserFeedbackItem] = useState<FeedbackItem | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const ratingTypes = ratingTypesMap.get(ratingSystem) ?? [];
  const [isVoting, setIsVoting] = useState<{ [key: string]: boolean }>(
    Object.fromEntries(ratingTypes.map((ratingType) => [ratingType, false])),
  );

  const appContext = useContext(AppContext);
  const userId = appContext.authnInfo?.userProfile.userId;

  useEffect(() => {
    const fetchFeedback = async () => {
      if (contentId && userId) {
        setIsFetching(true);
        try {
          const [feedbackStatResponse, userFeedbackItemResponse] = await Promise.all([
            feedbackServiceClient.listFeedback([contentId], ratingSystem, feedbackSource),
            feedbackServiceClient.getFeedbackByUser(contentId, userId, feedbackSource),
          ]);
          setFeedbackStatistics(feedbackStatResponse[0] ?? null);
          setUserFeedbackItem(userFeedbackItemResponse);
        } finally {
          setIsFetching(false);
        }
      }
    };
    fetchFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideFeedbackModal = () => setShowFeedbackModal(false);

  const handleVote = async (feedbackRating: FeedbackRating) => {
    // The buttons are only shown to signed-in users
    if (!userId) return;

    let updatedFeedbackStatistics: FeedbackItemStatistics;
    isVoting[feedbackRating] = true;
    setIsVoting({ ...isVoting });
    try {
      if (feedbackStatistics?.hasRated) {
        updatedFeedbackStatistics = await feedbackServiceClient.deleteFeedback(
          contentId,
          ratingSystem,
          feedbackSource,
        );
        if (userFeedbackItem?.Rating !== feedbackRating) {
          updatedFeedbackStatistics = await feedbackServiceClient.submitFeedback(
            contentId,
            userId,
            feedbackRating,
            ratingSystem,
            resourceType,
            { metadata: FeedbackMetadata.create(workflowId), feedbackSource },
          );
        }
      } else {
        updatedFeedbackStatistics = await feedbackServiceClient.submitFeedback(
          contentId,
          userId,
          feedbackRating,
          ratingSystem,
          resourceType,
          { metadata: FeedbackMetadata.create(workflowId), feedbackSource },
        );
      }
      setFeedbackStatistics(updatedFeedbackStatistics);
      const userFeedbackItemResponse = await feedbackServiceClient.getFeedbackByUser(
        contentId,
        userId,
        feedbackSource,
      );
      setUserFeedbackItem(userFeedbackItemResponse);
      if (onRating) {
        onRating(contentId, feedbackRating);
      }
      if (updatedFeedbackStatistics.hasRated && feedbackSource !== FeedbackSource.Collection) {
        setShowFeedbackModal(true);
      }
    } catch (e) {
      localLog.error(e);
    } finally {
      isVoting[feedbackRating] = false;
      setIsVoting({ ...isVoting });
    }
  };

  const hasRated = (feedbackRating: FeedbackRating) => {
    return feedbackStatistics?.hasRated && userFeedbackItem?.Rating === feedbackRating;
  };

  if (isFetching) {
    return <Spinner size="normal" />;
  }

  const ratingCounts = feedbackStatistics?.counts || [];
  const ratingButtons = ratingTypes.map((ratingType) => (
    <span key={ratingType}>
      <Button
        loading={isVoting[ratingType]}
        variant="icon"
        disabled={ratingTypes.some((rType) => isVoting[rType])}
        iconUrl={
          hasRated(ratingType) ? votedSvgPathMap.get(ratingType) : unvotedSvgPathMap.get(ratingType)
        }
        iconAlt={`${ratingType} feedback`}
        ariaLabel={`${ratingType} feedback`}
        onClick={() => handleVote(ratingType)}
      />
      {ratingCounts.find((rc) => rc.value === ratingType)?.count ?? '0'}
    </span>
  ));
  return (
    <>
      {ratingButtons}
      {requestWrittenFeedback && (
        <FeedbackModal
          ratingType={userFeedbackItem?.Rating as FeedbackRating}
          showFeedbackModal={showFeedbackModal}
          hideFeedbackModal={hideFeedbackModal}
          contentId={contentId}
        />
      )}
    </>
  );
};

export default ContentFeedback;
